import {
  Avatar,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  InputAdornment,
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  CircularProgress,
  Switch,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { SignatureEditor } from "../components/SignatureEditor";
import Container from "../base/Container";
import LockIcon from "@mui/icons-material/Lock";
import EmailIcon from "@mui/icons-material/Email";
import SmsIcon from "@mui/icons-material/Sms";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { DataContext } from "../contexts/DataProvider";
import {
  fetchPersonalDetails,
  fetchSignature as fetchSignatureRequest,
  requestPasswordOTP,
  changePasswordWithOTP,
  updateProfile,
} from "../services/users.service";
import { fetchOrganisations as fetchOrganisationsRequest } from "../services/organisations.service";
import Joyride from "react-joyride";
import { useUserPageTour } from "../hooks/useUserPageTour";
import { userPageTourSteps } from "../tours";
import { useAlert } from "../functions/useAlert";
import IntegrationsManager from "../components/integrations/IntegrationsManager ";

import { EXPERIMENTAL_FEATURES } from "../constants/experimentalFeatures";
import { useResendCooldown } from "./hooks/useResendCooldown";

const UserPage = () => {
  const [newPassword, setNewPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [open, setOpen] = useState(false);
  const [otpRequested, setOtpRequested] = useState(false);
  const [otpMessage, setOtpMessage] = useState("");
  const [otpError, setOtpError] = useState("");
  const [otpSending, setOtpSending] = useState(false);
  // Saved per-user preference, and the channel the last code actually went out on.
  const [otpChannel, setOtpChannel] = useState<"email" | "sms">("email");
  const [sentChannel, setSentChannel] = useState<"email" | "sms">("email");
  const [channelSaving, setChannelSaving] = useState(false);
  const {
    secondsLeft: resendSecondsLeft,
    isCoolingDown: resendCoolingDown,
    start: startResendCooldown,
    reset: resetResendCooldown,
  } = useResendCooldown(30);

  const { login, setLogin }: any = useContext(DataContext);
  const [image, setImage] = useState("");
  const [organisationList, setOrganisationsList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setAlert] = useAlert();

  const [emailSignature, setEmailSignature] = useState("");
  const [signatureEnabled, setSignatureEnabled] = useState(false);

  const [experimentalToggles, setExperimentalToggles] = useState<string[]>(
    login?.data?.experimentalFeatures ?? [],
  );
  const [experimentalSaving, setExperimentalSaving] = useState(false);

  useEffect(() => {
    setExperimentalToggles(login?.data?.experimentalFeatures ?? []);
  }, [login?.data?.experimentalFeatures]);

  useEffect(() => {
    setOtpChannel(
      login?.data?.passwordChangeOtpChannel === "sms" ? "sms" : "email",
    );
  }, [login?.data?.passwordChangeOtpChannel]);

  // Hash-based scroll — retries until the target element appears in the DOM.
  // Needed because some sections (e.g. calendar-sync-section) are conditionally
  // rendered after an async fetch and won't exist on first paint.
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;
    const targetId = hash.slice(1);
    let attempts = 0;
    const tryScroll = () => {
      const el = document.getElementById(targetId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      if (++attempts < 30) setTimeout(tryScroll, 100);
    };
    setTimeout(tryScroll, 150);
  }, []);

  const orgId = login.data.orgId;
  const userId = login.data.id;

  // Form Data State
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    mobile: "",
  });

  // Joyride Tour State
  const { runTour, tourStepIndex, handleJoyrideCallback } = useUserPageTour();

  useEffect(() => {
    if (login?.data) {
      setFormData({
        firstName: login.data.firstName || "",
        lastName: login.data.lastName || "",
        mobile: login.data.mobile || "",
      });
    }
  }, [login]);

  useEffect(() => {
    const fetchImage = async () => {
      try {
        const res: any = await fetchPersonalDetails(orgId);
        const matchingUser = res.find(
          (user: any) => user.userId === login.data.id,
        );
        if (matchingUser) setImage(matchingUser.image);
      } catch (err) {
        console.error("Error fetching image:", err);
      } finally {
        setLoading(false);
      }
    };

    const fetchSignature = async () => {
      try {
        const res: any = await fetchSignatureRequest();
        if (!res?.hasError) {
          setEmailSignature(res.emailSignature ?? "");
          setSignatureEnabled(res.emailSignatureEnabled ?? false);
        }
      } catch (err) {
        console.error("Error fetching signature:", err);
      }
    };

    fetchImage();
    fetchOrganisations();
    fetchSignature();
  }, [userId, orgId, login?.data?.id]);

  const fetchOrganisations = async () => {
    try {
      const organisation_data: any = await fetchOrganisationsRequest();
      setOrganisationsList(organisation_data);
    } catch (error) {
      console.error("Error fetching organisations:", error);
    }
  };

  // Reflects the live form field, so the hint clears as soon as a number is
  // typed — but the saved profile is what the backend actually validates.
  const hasMobileOnFile =
    (formData.mobile || "").trim() !== "" ||
    (login?.data?.mobile || "").trim() !== "";

  const handleOtpChannelChange = async (nextChannel: "email" | "sms") => {
    if (nextChannel === otpChannel || channelSaving) return;

    const previousChannel = otpChannel;
    setOtpChannel(nextChannel); // optimistic — reverted below if the save fails
    setOtpError("");
    setChannelSaving(true);
    try {
      const res: any = await updateProfile({
        passwordChangeOtpChannel: nextChannel,
      });
      if (res?.hasError) {
        setOtpChannel(previousChannel);
        setOtpError(res.message || "Failed to save your verification method");
        return;
      }
      if (res?.token) localStorage.setItem("token", res.token);
      if (res?.data) {
        setLogin((prev: any) => ({
          ...prev,
          data: { ...prev.data, ...res.data },
        }));
      }
      setAlert(
        nextChannel === "sms"
          ? "Verification codes will now be sent by SMS"
          : "Verification codes will now be sent by email",
        "success",
      );
    } catch (err: any) {
      setOtpChannel(previousChannel);
      setOtpError(
        err?.data?.message ||
          err?.data?.error ||
          "Failed to save your verification method",
      );
    } finally {
      setChannelSaving(false);
    }
  };

  const handleRequestOtp = async () => {
    if (otpSending || resendCoolingDown) return;
    setOtpMessage("");
    setOtpError("");
    setOtpSending(true);
    try {
      const res: any = await requestPasswordOTP(login.data.id);
      if (res && res.message) {
        setOtpRequested(true);
        // Trust the channel the backend actually delivered on rather than the
        // locally-selected preference — they diverge if the save didn't persist.
        const sentBySms = res.channel === "sms";
        setSentChannel(sentBySms ? "sms" : "email");
        setOtpMessage(
          res.destination
            ? `Verification code sent to ${res.destination}.`
            : sentBySms
              ? "Verification code sent to your mobile."
              : "Verification code sent to your email."
        );
        startResendCooldown();
      } else {
        setOtpError(res?.error || "Failed to send the verification code");
      }
    } catch (err: any) {
      setOtpError(
        err?.data?.error || err?.message || "Failed to send the verification code"
      );
    } finally {
      setOtpSending(false);
    }
  };

  // Abandon an in-progress change and return to the start. The code stays valid
  // server-side until it expires, but clearing the fields means nothing
  // half-entered is left sitting on screen — and re-requesting issues a new one.
  const handleCancelOtp = () => {
    setOtpRequested(false);
    setOtp("");
    setNewPassword("");
    setOtpMessage("");
    setOtpError("");
    resetResendCooldown();
  };

  const handleChangePasswordWithOtp = async () => {
    setOtpMessage("");
    setOtpError("");
    try {
      const res: any = await changePasswordWithOTP({ userId: login.data.id, otp, newPassword });
      if (res && res.message === "Password changed successfully") {
        setOpen(true);
        setOtp("");
        setNewPassword("");
        setOtpRequested(false);
        resetResendCooldown();
      } else if (res && res.error) {
        setOtpError(res.error);
      } else {
        setOtpError("Failed to change password");
      }
    } catch (err) {
      setOtpError("Failed to change password");
    }
  };

  const handleInputChange =
    (prop: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData({ ...formData, [prop]: event.target.value });
    };

  const handleSaveSignature = async () => {
    try {
      const res: any = await updateProfile({
        emailSignature,
        emailSignatureEnabled: signatureEnabled,
      });
      if (res?.hasError) {
        setAlert(res.message || "Failed to save signature", "error");
        return;
      }
      setAlert("Signature saved successfully!", "success");
    } catch (err) {
      setAlert("Failed to save signature", "error");
    }
  };

  const handleToggleSignature = async (enabled: boolean) => {
    setSignatureEnabled(enabled);
    try {
      const res: any = await updateProfile({
        emailSignatureEnabled: enabled,
      });
      if (res?.hasError) {
        setSignatureEnabled(!enabled);
        setAlert(res.message || "Failed to update signature", "error");
        return;
      }
      setAlert(enabled ? "Signature enabled" : "Signature disabled", "success");
    } catch (err) {
      setSignatureEnabled(!enabled);
      setAlert("Failed to update signature", "error");
    }
  };

  const handleSavePersonalDetails = async () => {
    try {
      const res: any = await updateProfile({
        firstName: formData.firstName,
        lastName: formData.lastName,
        mobile: formData.mobile,
      });

      if (res?.hasError) {
        setAlert(res.message || "Failed to update profile", "error");
        return;
      }

      // Refresh the token and login context so data stays in sync
      if (res?.token) {
        localStorage.setItem("token", res.token);
      }
      if (res?.data) {
        setLogin((prev: any) => ({
          ...prev,
          data: { ...prev.data, ...res.data },
        }));
      }

      setAlert("Profile updated successfully!", "success");
    } catch (err) {
      setAlert("Failed to update profile", "error");
    }
  };

  const handleMasterToggle = (checked: boolean) => {
    const newToggles = checked ? EXPERIMENTAL_FEATURES.map((f) => f.id) : [];
    setExperimentalToggles(newToggles);
    saveExperimentalFeatures(newToggles);
  };

  const handleIndividualToggle = (featureId: string, checked: boolean) => {
    const newToggles = checked
      ? [...experimentalToggles, featureId]
      : experimentalToggles.filter((id) => id !== featureId);
    setExperimentalToggles(newToggles);
    saveExperimentalFeatures(newToggles);
  };

  const saveExperimentalFeatures = async (newToggles: string[]) => {
    setExperimentalSaving(true);
    try {
      const res: any = await updateProfile({ experimentalFeatures: newToggles });
      if (res?.hasError) {
        setAlert(res.message || "Failed to save preferences", "error");
        return;
      }
      if (res?.token) localStorage.setItem("token", res.token);
      if (res?.data) {
        setLogin((prev: any) => ({
          ...prev,
          data: { ...prev.data, ...res.data },
        }));
      }
    } catch (err) {
      setAlert("Failed to save preferences", "error");
    } finally {
      setExperimentalSaving(false);
    }
  };

  return (
    <Container>
      <Joyride
        steps={userPageTourSteps}
        run={runTour}
        stepIndex={tourStepIndex}
        callback={handleJoyrideCallback}
        continuous
        showProgress
        showSkipButton
        disableOverlayClose={true}
        disableScrolling={true}
        styles={{ options: { primaryColor: "#007bff", zIndex: 10000 } }}
      />

      {/* Header & Avatar Section */}
      <Box
        id="profile-header-box"
        p={3}
        borderRadius="12px"
        boxShadow="rgba(0, 0, 0, 0.07) 0px 10px 36px 0px"
        position="relative"
        mb={4}
      >
        <Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h5" fontWeight="bold">
              My Profile
            </Typography>
          </Box>

          <Box
            sx={{
              background: "linear-gradient(to right, #007bff, #4da8ff)",
              height: 160,
              borderRadius: "12px",
            }}
          ></Box>

          <Box
            id="profile-avatar-container"
            position="relative"
            width="auto"
            height={160}
            mx="auto"
            mt={-8}
            textAlign="center"
            display="flex"
            flexDirection="column"
            alignItems="center"
          >
            {loading ? (
              <CircularProgress />
            ) : (
              <>
                <Box position="relative" display="flex" justifyContent="center">
                  <Avatar
                    src={image || "https://via.placeholder.com/100"}
                    sx={{ width: 120, height: 120, border: "4px solid white" }}
                  />
                  {/* Image upload — not yet implemented
                  <IconButton
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      backgroundColor: "white",
                      border: "1px solid #ccc",
                      zIndex: 1,
                    }}
                    onClick={() => fileRef.current?.click()}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleFileChange}
                  />
                  */}
                </Box>
                <Typography variant="h6" fontWeight="bold" mt={1}>
                  {login.data.name?.split(" ").map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ")}
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* Personal Info Section */}
      <Box
        id="personal-information-section"
        mt={3}
        p={3}
        bgcolor="white"
        borderRadius="12px"
        boxShadow={1}
      >
        <Typography variant="h6" fontWeight="bold" mb={2}>
          Personal Information
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="First Name"
              value={formData.firstName}
              onChange={handleInputChange("firstName")}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Last Name"
              value={formData.lastName}
              onChange={handleInputChange("lastName")}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              disabled
              fullWidth
              label="Organisation"
              InputLabelProps={{ shrink: true }}
              value={
                organisationList?.organisations?.find(
                  (org: any) => org._id === login.data.orgId,
                )?.name || "-"
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              disabled
              fullWidth
              label="Email"
              value={login.data?.email || ""}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Phone Number"
              value={formData.mobile}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  mobile: e.target.value.replace(/\D/g, ""),
                })
              }
              inputProps={{ inputMode: "numeric" }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
        <Box textAlign="right" mt={3}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSavePersonalDetails}
          >
            Save Details
          </Button>
        </Box>
      </Box>

      {/* Password Section */}
      <Box
        id="password-section"
        mt={4}
        p={3}
        bgcolor="white"
        borderRadius="12px"
        boxShadow={1}
      >
        <Typography variant="h6" fontWeight="bold" mb={2}>
          Security & Password
        </Typography>
        <Paper elevation={0} sx={{ p: 2, border: "1px solid #eee" }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="medium">
            Change Password
          </Typography>

          <Box mb={2}>
            <Typography variant="body2" color="text.secondary" mb={1}>
              How would you like to receive your verification code?
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="large"
              value={otpChannel}
              onChange={(_e, value) => {
                if (value) handleOtpChannelChange(value);
              }}
              disabled={channelSaving || otpRequested}
              sx={{
                "& .MuiToggleButton-root": {
                  px: 4,
                  py: 1.5,
                  fontSize: "0.875rem",
                  textTransform: "none",
                  minWidth: 140,
                },
              }}
            >
              <ToggleButton value="email">
                <EmailIcon sx={{ mr: 1 }} />
                Email
              </ToggleButton>
              <ToggleButton value="sms">
                <SmsIcon sx={{ mr: 1 }} />
                SMS
              </ToggleButton>
            </ToggleButtonGroup>

            {!hasMobileOnFile && (
              <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                Add a mobile number to your profile above to enable SMS verification.
              </Typography>
            )}
            {otpRequested && (
              <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                Finish or cancel the current password change to switch method.
              </Typography>
            )}
          </Box>

          {!otpRequested ? (
            <Box mt={2}>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleRequestOtp}
                disabled={otpSending}
              >
                {otpSending
                  ? "Sending…"
                  : otpChannel === "sms"
                    ? "Text me a verification code"
                    : "Email me a verification code"}
              </Button>
              {otpMessage && (
                <Typography color="success.main" mt={2}>
                  {otpMessage}
                </Typography>
              )}
              {otpError && (
                <Typography color="error.main" mt={2}>
                  {otpError}
                </Typography>
              )}
            </Box>
          ) : (
            <Box mt={2}>
              <Grid container spacing={2} mb={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={
                      sentChannel === "sms"
                        ? "Enter the code sent to your mobile"
                        : "Enter the code sent to your email"
                    }
                    fullWidth
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {sentChannel === "sms" ? <SmsIcon /> : <EmailIcon />}
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="New Password"
                    type="password"
                    fullWidth
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
              <Box display="flex" alignItems="center" gap={2}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleChangePasswordWithOtp}
                >
                  Confirm Password Change
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleRequestOtp}
                  disabled={resendCoolingDown || otpSending}
                >
                  {resendCoolingDown
                    ? `Resend code in ${resendSecondsLeft}s`
                    : otpSending
                      ? "Sending…"
                      : "Resend code"}
                </Button>
                <Button
                  variant="text"
                  color="inherit"
                  onClick={handleCancelOtp}
                  disabled={otpSending}
                >
                  Cancel
                </Button>
              </Box>
              {otpMessage && (
                <Typography color="success.main" mt={2}>
                  {otpMessage}
                </Typography>
              )}
              {otpError && (
                <Typography color="error.main" mt={2}>
                  {otpError}
                </Typography>
              )}
            </Box>
          )}
        </Paper>
      </Box>

      {/* Integrations Module */}
      <Box
        id="integrations-section"
        mt={4}
        p={3}
        bgcolor="white"
        borderRadius="12px"
        boxShadow={1}
      >
        <Typography variant="h6" fontWeight="bold" mb={3}>
          Integrations
        </Typography>
        <IntegrationsManager />
      </Box>

      {/* Email Signature Section */}
      <Box
        id="email-signature-section"
        mt={4}
        p={3}
        bgcolor="white"
        borderRadius="12px"
        boxShadow={1}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" fontWeight="bold">
            Email Signature
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={signatureEnabled}
                onChange={(e) => handleToggleSignature(e.target.checked)}
                color="primary"
              />
            }
            label={signatureEnabled ? "Enabled" : "Disabled"}
          />
        </Box>
        <Typography variant="body2" color="text.secondary" mb={2}>
          This signature will be automatically appended to composed and
          AI-generated emails.
        </Typography>
        <SignatureEditor value={emailSignature} onChange={setEmailSignature} />
        <Box textAlign="right" mt={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveSignature}
          >
            Save Signature
          </Button>
        </Box>
      </Box>

      {/* Experimental Features Section */}
      <Box
        id="experimental-features-section"
        mt={4}
        mb={4}
        p={3}
        bgcolor="white"
        borderRadius="12px"
        boxShadow={1}
      >
        {/* Header row */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          <Typography variant="h6" fontWeight="bold">
            Experimental Features
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2">Enable All</Typography>
            <Switch
              checked={
                experimentalToggles.length === EXPERIMENTAL_FEATURES.length
              }
              onChange={(e) => handleMasterToggle(e.target.checked)}
              disabled={experimentalSaving}
            />
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary" mb={2}>
          Opt-in to beta features before they are widely released. These
          features are in active development and may change.
        </Typography>

        {/* Individual feature rows */}
        {EXPERIMENTAL_FEATURES.map((feature, index) => (
          <Box
            key={feature.id}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            p={2}
            sx={{
              borderTop: "1px solid #eee",
              borderBottom:
                index === EXPERIMENTAL_FEATURES.length - 1
                  ? "1px solid #eee"
                  : "none",
            }}
          >
            <Box>
              <Typography fontWeight="bold">{feature.label}</Typography>
              <Typography variant="body2" color="text.secondary">
                {feature.description}
              </Typography>
            </Box>
            <Switch
              checked={experimentalToggles.includes(feature.id)}
              onChange={(e) =>
                handleIndividualToggle(feature.id, e.target.checked)
              }
              disabled={experimentalSaving}
            />
          </Box>
        ))}
      </Box>

      {/* Success Modal */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <Box display="flex" justifyContent="center" mt={3}>
          <CheckCircleOutlineIcon sx={{ fontSize: 60, color: "green" }} />
        </Box>
        <DialogContent>
          <Typography variant="h6" align="center" fontWeight="medium" mt={1}>
            Password Changed Successfully!
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", mb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpen(false)}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UserPage;
